var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faRightToBracket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { Accordion, AccordionDetails, AccordionSummary, Button, FormControlLabel, Link, Radio, Stack, Typography, accordionSummaryClasses, formControlLabelClasses, inputBaseClasses, outlinedInputClasses, styled } from '@mui/material';
import { get } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { DifferenceMode, PlanType, StatusPermission } from '../../../enum';
import { Autocomplete, FormField, MultiAutocomplete, NumberInput, RadioGroup } from '../../common';
import { useQueueAPI } from '../queue/selectors';
import { useDetailTimeline, useEffectiveCapacityPermission, useIsPlanLocked, useLocationOptions, usePeriodLabelTimeline, usePeriods, usePlanType, useQueueLocations } from '../selectors';
import { ShrinkageUnitMode } from '../shrinkage/useShrinkageUnit';
var StyledAccordion = styled(Accordion)(function (_a) {
  var theme = _a.theme;
  return {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    gap: theme.spacing(2)
  };
});
var StyledAccordionSummary = styled(AccordionSummary)(function (_a) {
  var _b;
  var theme = _a.theme;
  return _b = {
    padding: theme.spacing(2, 0, 0, 0),
    alignItems: 'flex-start',
    flex: '0 0 auto',
    minWidth: theme.spacing(4)
  }, _b[".".concat(accordionSummaryClasses.content)] = {
    margin: 0
  }, _b[".".concat(accordionSummaryClasses.expandIconWrapper)] = {
    flexShrink: 0
  }, _b[".".concat(accordionSummaryClasses.expandIconWrapper, ".Mui-expanded")] = {
    transform: 'rotateY(180deg)',
    transition: 'transform 0.4s'
  }, _b;
});
var StyledFormControlLabel = styled(FormControlLabel)(function (_a) {
  var _b;
  var theme = _a.theme;
  return _b = {
    marginRight: theme.spacing(0)
  }, _b[".".concat(formControlLabelClasses.label)] = {
    fontSize: theme.typography.body2.fontSize
  }, _b;
});
var ExpandIcon = styled(FontAwesomeIcon)(function (_a) {
  var theme = _a.theme;
  return {
    color: theme.palette.primary.main
  };
});
var StyledAutocomplete = styled(Autocomplete)(function (_a) {
  var _b;
  var theme = _a.theme;
  return _b = {
    width: '10.5rem'
  }, _b[".".concat(outlinedInputClasses.root, ".MuiInputBase-sizeSmall")] = {
    padding: theme.spacing(1.5, 6, 1.5, 0.5)
  }, _b[".".concat(inputBaseClasses.input)] = {
    fontSize: theme.typography.body2.fontSize
  }, _b;
});
var StyledAccordionDetails = styled(AccordionDetails)(function (_a) {
  var _b;
  var theme = _a.theme;
  return _b = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 0, 1)
  }, _b[".".concat(inputBaseClasses.root)] = {
    fontSize: theme.typography.body2.fontSize
  }, _b[".".concat(inputBaseClasses.input)] = {
    padding: theme.spacing(2)
  }, _b;
});
var renderPeriodOption = function (props, option) {
  var name = option.name;
  return _jsx("li", __assign({}, props, {
    children: _jsx(Typography, __assign({
      variant: "body2"
    }, {
      children: name
    }))
  }));
};
export var buildMonthlyBulkChangeOptions = function (periods, monthFormat) {
  if (periods === void 0) {
    periods = [];
  }
  if (monthFormat === void 0) {
    monthFormat = 'MMMM YYYY';
  }
  return periods.map(function (period, id) {
    return {
      id: id,
      name: period.format(monthFormat)
    };
  }).sort(function (a, b) {
    return a.id % 12 - b.id % 12;
  });
};
export var BulkChangeAccordion = function (_a) {
  var _b;
  var _c = _a.name,
    name0 = _c[0],
    name1 = _c[1],
    title = _a.title,
    setShowCheckbox = _a.setShowCheckbox,
    _d = _a.max,
    max = _d === void 0 ? 9999999 : _d,
    _e = _a.min,
    min = _e === void 0 ? 1 : _e,
    _f = _a.decimal,
    decimal = _f === void 0 ? 0 : _f,
    selectedRows = _a.selectedRows,
    getValues = _a.getValues,
    setValue = _a.setValue,
    selectedQueueLocationId = _a.selectedQueueLocationId,
    unitMode = _a.unitMode,
    _g = _a.usePeriodLabels,
    usePeriodLabels = _g === void 0 ? false : _g;
  var t = useTranslation('wizard', {
    keyPrefix: 'bulkChange'
  }).t;
  var tTranslation = useTranslation('translation').t;
  var planType = usePlanType();
  var locations = useQueueLocations();
  var periods = usePeriods();
  var detailTimelines = useDetailTimeline();
  var periodLabelTimelines = usePeriodLabelTimeline();
  var initialValuesRef = useRef();
  var capacityPermission = useEffectiveCapacityPermission();
  var queueData = useQueueAPI()[0];
  var locationOptions = useLocationOptions();
  var isVolumes = name0 === 'volumes';
  var isShrinkages = name0 === 'shrinkages';
  var isWorkVolumeSection = ['volume', 'productionHour', 'prodOccupancy'].includes(name1);
  var isPlanLocked = useIsPlanLocked();
  var monthlyOptions = useMemo(function () {
    var translatedMonthFormat = tTranslation('dateFormat.longMonthYearFormat', {
      defaultValue: 'MMMM YYYY'
    });
    var monthFormat = translatedMonthFormat.includes('longMonthYearFormat') ? 'MMMM YYYY' : translatedMonthFormat;
    return buildMonthlyBulkChangeOptions(periods !== null && periods !== void 0 ? periods : [], monthFormat);
  }, [periods, tTranslation]);
  var options = useMemo(function () {
    var _a, _b;
    var timeline = usePeriodLabels ? periodLabelTimelines : detailTimelines;
    var titles = (_b = (_a = timeline[0]) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : [];
    if (usePeriodLabels) return titles.map(function (name, id) {
      return {
        id: id,
        name: name
      };
    }).filter(function (_a) {
      var name = _a.name;
      return !!name;
    });
    if ([PlanType.Monthly, PlanType.MonthlyCustom].includes(planType)) return monthlyOptions;
    return titles.map(function (name, id) {
      return {
        id: id,
        name: name
      };
    }).filter(function (_a) {
      var name = _a.name;
      return !!name;
    });
  }, [detailTimelines, monthlyOptions, periodLabelTimelines, planType, usePeriodLabels]);
  var validateForm = yup.object().shape({
    valueChange: yup.number().nullable().required(),
    startDate: yup.number().nullable().required().test('startDate', 'startDateInvalid', function (value, context) {
      var endDate = context.parent.endDate;
      return value <= endDate;
    }),
    endDate: yup.number().nullable().required().test('endDate', 'endDateInvalid', function (value, context) {
      var startDate = context.parent.startDate;
      return value >= startDate;
    })
  });
  var methods = useForm({
    defaultValues: {
      valueChange: 0,
      startDate: 0,
      endDate: options.length - 1,
      differenceMode: DifferenceMode.Percentage,
      selectedLocations: []
    },
    resolver: yupResolver(validateForm)
  });
  var control = methods.control,
    handleSubmit = methods.handleSubmit,
    reset = methods.reset,
    setLocalValue = methods.setValue,
    watch = methods.watch;
  var selectedLocations = (_b = watch('selectedLocations')) !== null && _b !== void 0 ? _b : [];
  useEffect(function () {
    setLocalValue('valueChange', 0);
  }, [min, max, setLocalValue]);
  useEffect(function () {
    initialValuesRef.current = structuredClone(getValues());
  }, [getValues]);
  var bulkUpdate = function (input) {
    var _a, _b, _c;
    var _d = input !== null && input !== void 0 ? input : {},
      _e = _d.startDate,
      startDate = _e === void 0 ? 0 : _e,
      _f = _d.endDate,
      endDate = _f === void 0 ? options.length - 1 : _f,
      _g = _d.valueChange,
      valueChange = _g === void 0 ? 0 : _g,
      differenceMode = _d.differenceMode;
    var setTargetValue = function (val, path) {
      var prop = name1;
      var originalValue = get(initialValuesRef.current, "".concat(path, ".").concat(prop));
      var currentValue = val[prop];
      var newValue = Number(currentValue) + valueChange;
      if (differenceMode === DifferenceMode.Percentage) {
        newValue = Number(currentValue) * (1 + valueChange / 100);
      }
      newValue = Math.round(newValue * Math.pow(10, decimal)) / Math.pow(10, decimal);
      var adjustedValue = Math.max(min, Math.min(max, newValue));
      newValue = adjustedValue === Number(currentValue) ? currentValue : adjustedValue;
      setValue("".concat(path, ".").concat(prop), input ? newValue : originalValue, {
        shouldDirty: true
      });
    };
    var applyToVolumes = function (basePath, targetVolumes) {
      if (targetVolumes === void 0) {
        targetVolumes = [];
      }
      targetVolumes.forEach(function (value, idx) {
        if (idx < startDate || idx > endDate) return;
        setTargetValue(value, "".concat(basePath, ".").concat(idx));
      });
    };
    var reviewData = getValues();
    var hasLocations = !!((_a = reviewData.queueLocations) === null || _a === void 0 ? void 0 : _a.length);
    var isReset = !input;
    var isResetAll = isReset && (!selectedRows || selectedRows.length === 0);
    if (isVolumes) {
      if (!isWorkVolumeSection && ((_b = reviewData.queueLocations) === null || _b === void 0 ? void 0 : _b.length)) {
        reviewData.queueLocations.forEach(function (location, locIdx) {
          if (isResetAll || selectedLocations.includes(location.id)) {
            applyToVolumes("queueLocations.".concat(locIdx, ".").concat(name0), location[name0]);
          }
        });
      } else {
        applyToVolumes(name0, reviewData[name0]);
      }
    } else if (isShrinkages) {
      (_c = reviewData === null || reviewData === void 0 ? void 0 : reviewData.shrinkages) === null || _c === void 0 ? void 0 : _c.forEach(function (value, idx) {
        if (idx < startDate || idx > endDate) return;
        if (hasLocations) {
          value.forEach(function (v, rowIdx) {
            if (v.queueLocationId !== selectedQueueLocationId) return;
            var isSelected = selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.includes(rowIdx);
            if (isResetAll || isSelected) {
              setTargetValue(v, "".concat(name0, ".").concat(idx, ".").concat(rowIdx));
            }
          });
        } else {
          value.forEach(function (v, rowIdx) {
            var isSelected = selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.includes(rowIdx);
            if (isResetAll || isSelected) {
              setTargetValue(v, "".concat(name0, ".").concat(idx, ".").concat(rowIdx));
            }
          });
        }
      });
    }
  };
  var handleApply = handleSubmit(function (input) {
    bulkUpdate(input);
  });
  var handleReset = function () {
    var _a;
    bulkUpdate();
    reset();
    if (name1 === 'attrition') setValue('attritionEntryMode', (_a = queueData === null || queueData === void 0 ? void 0 : queueData.data) === null || _a === void 0 ? void 0 : _a.attritionEntryMode);
  };
  var handleAccordionChange = function (event, expanded) {
    setShowCheckbox === null || setShowCheckbox === void 0 ? void 0 : setShowCheckbox(expanded);
  };
  var disableApply = selectedRows && !selectedRows.length || !isWorkVolumeSection && isVolumes && !!locations.length && !selectedLocations.length || isShrinkages && unitMode !== ShrinkageUnitMode.Percent;
  return _jsx(Stack, __assign({
    direction: {
      xs: 'column',
      lg: 'row'
    },
    gap: 2,
    flexWrap: "wrap",
    alignItems: {
      xs: 'stretch',
      lg: 'center'
    },
    sx: {
      minWidth: 0,
      maxWidth: '100%'
    }
  }, {
    children: _jsxs(StyledAccordion, __assign({
      disableGutters: true,
      elevation: 0,
      onChange: function (event, expanded) {
        return handleAccordionChange(event, expanded);
      }
    }, {
      children: [_jsxs(Stack, __assign({
        display: "flex",
        direction: "row",
        alignItems: "center",
        sx: {
          flexShrink: 0,
          minWidth: 'fit-content'
        }
      }, {
        children: [_jsx(Typography, __assign({
          variant: "subtitle1",
          mb: 3,
          mr: 2,
          sx: {
            flexShrink: 0
          }
        }, {
          children: title
        })), _jsx(StyledAccordionSummary, {
          expandIcon: _jsx(ExpandIcon, {
            icon: faRightToBracket,
            size: "lg"
          }),
          disabled: capacityPermission !== StatusPermission.Edit || isPlanLocked
        })]
      })), _jsxs(StyledAccordionDetails, {
        children: [_jsx(Typography, __assign({
          sx: {
            pt: 2
          },
          variant: "body2"
        }, {
          children: t('changeAllValues')
        })), _jsx(FormField, __assign({
          required: true,
          name: "valueChange",
          control: control
        }, {
          children: _jsx(NumberInput, {
            max: max,
            decimal: decimal,
            min: -max,
            sx: {
              maxWidth: '4rem'
            }
          })
        })), _jsx(FormField, __assign({
          required: true,
          name: "differenceMode",
          control: control
        }, {
          children: _jsxs(RadioGroup, __assign({
            row: true,
            sx: {
              flexWrap: 'wrap',
              gap: 2
            }
          }, {
            children: [_jsx(StyledFormControlLabel, {
              value: DifferenceMode.Percentage,
              label: t('percentage'),
              control: _jsx(Radio, {})
            }), _jsx(StyledFormControlLabel, {
              value: DifferenceMode.Absolute,
              label: t('absolute'),
              control: _jsx(Radio, {})
            })]
          }))
        })), _jsx(FormField, __assign({
          required: true,
          name: "startDate",
          control: control
        }, {
          children: _jsx(StyledAutocomplete, {
            options: options,
            placeholder: t('placeholder', {
              planType: planType
            }),
            renderOption: renderPeriodOption
          })
        })), _jsx(Typography, __assign({
          sx: {
            pt: 2
          },
          variant: "body2"
        }, {
          children: t('to')
        })), _jsx(FormField, __assign({
          required: true,
          name: "endDate",
          control: control
        }, {
          children: _jsx(StyledAutocomplete, {
            options: options,
            placeholder: t('placeholder', {
              planType: planType
            }),
            renderOption: renderPeriodOption
          })
        })), _jsxs(Stack, __assign({
          direction: "row",
          gap: 3,
          alignItems: "center",
          flexWrap: "wrap",
          sx: {
            minWidth: 0
          }
        }, {
          children: [!isWorkVolumeSection && isVolumes && !!locations.length && _jsxs(Stack, __assign({
            direction: "row",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            sx: {
              minWidth: 0
            }
          }, {
            children: [_jsx(Typography, __assign({
              variant: "body2",
              sx: {
                whiteSpace: 'nowrap'
              }
            }, {
              children: t('chooseLocation')
            })), _jsx(FormField, __assign({
              name: "selectedLocations",
              control: control
            }, {
              children: _jsx(MultiAutocomplete, {
                options: locationOptions,
                sx: {
                  minWidth: '12rem'
                }
              })
            }))]
          })), _jsx(Button, __assign({
            variant: "outlined",
            onClick: handleApply,
            disabled: disableApply,
            size: "small"
          }, {
            children: t('apply')
          })), _jsx(Link, __assign({
            component: "button",
            type: "button",
            onClick: handleReset,
            variant: "body2"
          }, {
            children: t('reset')
          }))]
        }))]
      })]
    }))
  }));
};